import { supabase } from "@/lib/supabase";
import { Incident, IncidentStatus } from "@/components/pages/restoration/types";

// NOTE: restoration_room_incidents is not yet in the auto-generated types file (types.ts).
// AE-54's migration will create the table. We use `as any` casts here to avoid type errors
// without regenerating the types file. These casts should be removed once the types are regenerated.

/** Per-user cache — never use a global key (v4 leaked data across accounts on shared browsers). */
const CACHE_KEY_PREFIX = "restoration_room_incidents_v5";
const LEGACY_CACHE_KEY = "restoration_room_incidents_v4";

const cacheKeyForUser = (userId: string) => `${CACHE_KEY_PREFIX}:${userId}`;

/** Call on sign-out so the next session never reads stale incident rows from localStorage. */
export const clearIncidentCache = (userId?: string): void => {
  try {
    if (userId) {
      localStorage.removeItem(cacheKeyForUser(userId));
    }
    localStorage.removeItem(LEGACY_CACHE_KEY);
  } catch {
    // localStorage may be unavailable
  }
};

class IncidentService {
  // ── Cache helpers ──────────────────────────────────────────────────────────

  private readCache(userId: string): Incident[] {
    try {
      const raw = localStorage.getItem(cacheKeyForUser(userId));
      if (!raw) return [];
      const parsed = JSON.parse(raw) as Incident[];
      // Belt-and-suspenders: only return rows owned by this user.
      return parsed.filter((i) => i.user_id === userId);
    } catch {
      return [];
    }
  }

  private writeCache(userId: string, incidents: Incident[]): void {
    try {
      const owned = incidents.filter((i) => i.user_id === userId);
      localStorage.setItem(cacheKeyForUser(userId), JSON.stringify(owned));
    } catch {
      // localStorage may be unavailable (e.g., private browsing quota exceeded)
      console.warn("[incidentService] Unable to write localStorage cache.");
    }
  }

  // ── Profile helper ─────────────────────────────────────────────────────────

  private async getUserContext(): Promise<{
    userId: string;
    schoolId: string | null;
    districtId: string | null;
  }> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    const { data: profile } = await supabase
      .from("profiles")
      .select("school_id, district_id")
      .eq("id", user.id)
      .maybeSingle();

    return {
      userId: user.id,
      schoolId: profile?.school_id ?? null,
      districtId: profile?.district_id ?? null,
    };
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  // Accepts an optional client-supplied `id`. This is used by the
  // draft-incident flow: the page generates a UUID up front and only
  // calls create() after the leader has entered enough identifying
  // detail. Passing the id keeps it stable from draft to persisted, so
  // the chat session does not reset when the row hits the database.
  // When omitted, the table's `gen_random_uuid()` default fires.
  async create(
    incident: Omit<Incident, "created_at" | "updated_at" | "user_id"> & { id?: string }
  ): Promise<Incident> {
    const { userId, schoolId, districtId } = await this.getUserContext();

    const { data, error } = await (supabase as any)
      .from("restoration_room_incidents")
      .insert({
        ...incident,
        user_id: userId,
        school_id: schoolId,
        district_id: districtId,
      })
      .select()
      .single();

    if (error) {
      console.error("[incidentService] create error:", error);
      throw error;
    }

    const created = data as Incident;

    const cached = this.readCache(userId);
    this.writeCache(userId, [created, ...cached.filter((i) => i.id !== created.id)]);

    return created;
  }

  async update(id: string, fields: Partial<Incident>): Promise<Incident> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    // Only send the caller-supplied fields; Supabase trigger handles updated_at
    const { data, error } = await (supabase as any)
      .from("restoration_room_incidents")
      .update(fields)
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error("[incidentService] update error:", error);
      throw error;
    }

    const updated = data as Incident;

    const cached = this.readCache(user.id);
    const idx = cached.findIndex((i) => i.id === id);
    if (idx !== -1) {
      cached[idx] = updated;
      this.writeCache(user.id, cached);
    }

    return updated;
  }

  async delete(id: string): Promise<void> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    const { error } = await (supabase as any)
      .from("restoration_room_incidents")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("[incidentService] delete error:", error);
      throw error;
    }

    const cached = this.readCache(user.id).filter((i) => i.id !== id);
    this.writeCache(user.id, cached);
  }

  async getById(id: string): Promise<Incident | null> {
    const { data, error } = await (supabase as any)
      .from("restoration_room_incidents")
      .select("*")
      .eq("id", id)
      .maybeSingle();

    if (error) {
      console.error("[incidentService] getById error:", error);
      throw error;
    }

    return (data as Incident) ?? null;
  }

  async listMine(filter?: { status?: IncidentStatus }): Promise<Incident[]> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    try {
      let query = (supabase as any)
        .from("restoration_room_incidents")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (filter?.status) {
        query = query.eq("status", filter.status);
      }

      const { data, error } = await query;

      if (error) throw error;

      const incidents = (data ?? []) as Incident[];
      this.writeCache(user.id, incidents);
      try {
        localStorage.removeItem(LEGACY_CACHE_KEY);
      } catch {
        // ignore
      }
      return incidents;
    } catch (err) {
      console.warn(
        "[incidentService] listMine fell back to localStorage cache due to Supabase error:",
        err
      );
      const cached = this.readCache(user.id);
      if (filter?.status) {
        return cached.filter((i) => i.status === filter.status);
      }
      return cached;
    }
  }

  async listSchool(
    schoolId: string,
    filter?: { status?: IncidentStatus }
  ): Promise<Incident[]> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    let query = (supabase as any)
      .from("restoration_room_incidents")
      .select("*")
      .eq("school_id", schoolId)
      .neq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (filter?.status) {
      query = query.eq("status", filter.status);
    }

    const { data, error } = await query;

    if (error) {
      console.error("[incidentService] listSchool error:", error);
      throw error;
    }

    const incidents = (data ?? []) as Incident[];

    // Batch-fetch profiles for creator names (Option B: client-side join)
    const userIds = [...new Set(incidents.map((i) => i.user_id).filter(Boolean))] as string[];
    if (userIds.length > 0) {
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, user_name")
        .in("id", userIds);

      const profileMap: Record<string, string> = {};
      for (const p of profiles ?? []) {
        profileMap[p.id] = p.user_name ?? "Unknown";
      }
      for (const inc of incidents) {
        inc.creator_name = inc.user_id ? (profileMap[inc.user_id] ?? "Unknown") : "Unknown";
      }
    }

    return incidents;
  }

  async searchByStudent(query: string): Promise<Incident[]> {
    const { data, error } = await (supabase as any)
      .from("restoration_room_incidents")
      .select("*")
      .ilike("student_identifier", `%${query}%`)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("[incidentService] searchByStudent error:", error);
      throw error;
    }

    return (data ?? []) as Incident[];
  }
}

export const incidentService = new IncidentService();
